/**
 * JWT Utility Functions
 * 
 * These utilities decode JWT tokens to extract claims that are cryptographically
 * signed by the server. This prevents tampering with role/permission data since
 * modifying JWT claims invalidates the signature.
 */

/**
 * Decode a JWT token and extract its payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} - The decoded payload or null if invalid
 */
export function decodeJwt(token) {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Base64Url decode (replace - with + and _ with /)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode base64 string
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Extract user claims from a JWT token
 * These claims are signed by the server and cannot be tampered with
 * @param {string} token - The JWT access token
 * @returns {object} - User claims including is_admin, is_admin_or_qa
 */
export function extractUserClaims(token) {
  const payload = decodeJwt(token);
  
  if (!payload) {
    return {
      is_admin: false,
      is_admin_or_qa: false,
    };
  }

  return {
    is_admin: payload.is_admin === true,
    is_admin_or_qa: payload.is_admin_or_qa === true,
  };
}

/**
 * Check if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if token is expired or invalid
 */
export function isTokenExpired(token) {
  const payload = decodeJwt(token);
  
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  return Date.now() >= payload.exp * 1000;
}
